/* ============================================================
   sc-booking-form.js — Multi-step booking form logic
   Step 1: Year / Make / Model / Service / Stage
   Step 2: Name / Phone / Email / Instagram / Date / Notes
   - Dynamic stage dropdown based on service selection
   - Per-step client-side validation
   - Progress bar animation between steps
   - Build summary recap shown on Step 2
   - Phone auto-formatting (###) ###-####
   - Year digit-only enforcement with range validation
   - Submits full JSON payload to webhook (Make.com → Trello)
   - Loading / success / error states
   ============================================================ */

(function () {
  'use strict';

  /* ── Stage options keyed by service name ── */
  var STAGE_OPTIONS = {
    'Ambient Lighting': [
      { value: 'Stage 01 — The Foundation ($399)',   label: 'STAGE 01 — THE FOUNDATION — $399' },
      { value: 'Stage 02 — The Full Build ($499)',   label: 'STAGE 02 — THE FULL BUILD — $499' },
      { value: 'Stage 03 — The Complete ($699)',     label: 'STAGE 03 — THE COMPLETE — $699' }
    ],
    'Window Tinting': [
      { value: 'Stage 01 — Nano Carbon ($199)',    label: 'STAGE 01 — NANO CARBON — $199' },
      { value: 'Stage 02 — Nano Ceramic ($299)',   label: 'STAGE 02 — NANO CERAMIC — $299' },
      { value: 'Stage 03 — Ceramic Pro ($449)',    label: 'STAGE 03 — CERAMIC PRO — $449' }
    ],
    'Vehicle Wraps':         [{ value: 'Waitlist', label: 'JOIN WAITLIST' }],
    'Paint Protection Film': [{ value: 'Waitlist', label: 'JOIN WAITLIST' }],
    'Apple CarPlay': [
      { value: 'Apple CarPlay Integration — Custom Quote (Starting at $299)', label: 'CARPLAY INTEGRATION — STARTING AT $299' }
    ],
    'Dashcam Install': [
      { value: 'Stage 01 — Front Camera ($150)',                    label: 'STAGE 01 — FRONT CAMERA — $150' },
      { value: 'Stage 02 — Front + Rear ($250)',                    label: 'STAGE 02 — FRONT + REAR — $250' },
      { value: 'Stage 03 — Front + Rear + Parking Mode ($350)',     label: 'STAGE 03 — FRONT + REAR + PARKING MODE — $350' }
    ],
    'Starlight Headliner': [
      { value: 'Sedan / Coupe — Starting at $799',     label: 'SEDAN / COUPE — STARTING AT $799' },
      { value: 'SUV / Crossover — Starting at $999',   label: 'SUV / CROSSOVER — STARTING AT $999' },
      { value: 'Truck / Van — Starting at $1,099',     label: 'TRUCK / VAN — STARTING AT $1,099' },
      { value: 'Luxury / Exotic — Starting at $1,299', label: 'LUXURY / EXOTIC — STARTING AT $1,299' }
    ]
  };

  /* ── Init a single form ── */
  function initForm(formEl) {
    if (!formEl || formEl._scInit) return;
    formEl._scInit = true;

    var card         = formEl.closest('.sc-booking-form__card');
    var successEl    = card ? card.querySelector('.sc-booking-form__success') : null;
    var formErr      = formEl.querySelector('.sc-booking-form__form-error');
    var serviceEl    = formEl.querySelector('[name="service"]');
    var stageEl      = formEl.querySelector('[name="stage"]');
    var dateEl       = formEl.querySelector('[name="date"]');
    var nextBtn      = formEl.querySelector('.sc-booking-form__next');
    var backBtn      = formEl.querySelector('.sc-booking-form__back');
    var submitBtn    = formEl.querySelector('.sc-booking-form__submit');
    var submitText   = formEl.querySelector('.sc-booking-form__submit-text');
    var step1El      = formEl.querySelector('[data-step="1"]');
    var step2El      = formEl.querySelector('[data-step="2"]');
    var progressDots = card ? card.querySelectorAll('[data-progress-step]') : [];
    var progressFill = card ? card.querySelector('.sc-booking-form__progress-fill') : null;
    var webhookUrl   = (formEl.dataset.webhook || '').trim();

    /* Build summary spans */
    var summaryVehicle = card ? card.querySelector('[data-summary="vehicle"]') : null;
    var summaryService = card ? card.querySelector('[data-summary="service"]') : null;
    var summaryStage   = card ? card.querySelector('[data-summary="stage"]') : null;

    /* Set minimum date to today */
    if (dateEl) {
      dateEl.min = new Date().toISOString().split('T')[0];
    }

    /* ── Update progress bar ── */
    function updateProgress(step) {
      progressDots.forEach(function (dot) {
        var n = parseInt(dot.dataset.progressStep, 10);
        dot.classList.remove(
          'sc-booking-form__progress-step--active',
          'sc-booking-form__progress-step--done'
        );
        if (n === step) {
          dot.classList.add('sc-booking-form__progress-step--active');
        } else if (n < step) {
          dot.classList.add('sc-booking-form__progress-step--done');
        }
      });
      if (progressFill) {
        progressFill.style.width = step >= 2 ? '100%' : '0%';
      }
    }

    /* ── Populate build summary on Step 2 ── */
    function populateSummary() {
      var yr  = ((formEl.querySelector('[name="year"]')    || {}).value || '').trim();
      var mk  = ((formEl.querySelector('[name="make"]')    || {}).value || '').trim();
      var mo  = ((formEl.querySelector('[name="model"]')   || {}).value || '').trim();
      var svc = ((formEl.querySelector('[name="service"]') || {}).value || '').trim();
      var stg = ((formEl.querySelector('[name="stage"]')   || {}).value || '').trim();

      if (summaryVehicle) summaryVehicle.textContent = [yr, mk, mo].filter(Boolean).join(' ') || '—';
      if (summaryService) summaryService.textContent = svc || '—';
      if (summaryStage)   summaryStage.textContent   = stg || '—';
    }

    /* ── Show a step (1 or 2) ── */
    function showStep(step) {
      if (step1El) step1El.style.display = step === 1 ? '' : 'none';
      if (step2El) step2El.style.display = step === 2 ? '' : 'none';
      if (step === 2) populateSummary();
      updateProgress(step);
      clearFormError();
      /* Scroll card into view so the top of the form is visible */
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* ── Populate stage dropdown ── */
    function refreshStages() {
      var service = serviceEl ? serviceEl.value : '';
      var opts    = STAGE_OPTIONS[service] || [];
      stageEl.innerHTML = '';

      if (!service) {
        stageEl.innerHTML = '<option value="">SELECT SERVICE FIRST</option>';
        return;
      }

      var placeholder = document.createElement('option');
      placeholder.value       = '';
      placeholder.textContent = opts.length === 1 ? opts[0].label : 'SELECT STAGE';
      stageEl.appendChild(placeholder);

      opts.forEach(function (opt) {
        var el       = document.createElement('option');
        el.value     = opt.value;
        el.textContent = opt.label;
        stageEl.appendChild(el);
      });

      /* Auto-select if only one option (waitlist services) */
      if (opts.length === 1) stageEl.value = opts[0].value;
    }

    if (serviceEl) serviceEl.addEventListener('change', refreshStages);
    refreshStages();

    /* ── Phone auto-format: (###) ###-#### ── */
    var phoneEl = formEl.querySelector('[name="phone"]');
    if (phoneEl) {
      phoneEl.addEventListener('input', function () {
        var digits = phoneEl.value.replace(/\D/g, '').slice(0, 10);
        var fmt    = '';
        if (digits.length > 6) {
          fmt = '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
        } else if (digits.length > 3) {
          fmt = '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
        } else if (digits.length > 0) {
          fmt = '(' + digits;
        }
        phoneEl.value = fmt;
      });
    }

    /* ── Year: digits only, 4 chars max, range check on blur ── */
    var yearEl = formEl.querySelector('[name="year"]');
    if (yearEl) {
      yearEl.addEventListener('input', function () {
        yearEl.value = yearEl.value.replace(/\D/g, '').slice(0, 4);
      });
      yearEl.addEventListener('blur', function () {
        if (yearEl.value.length !== 4) return;
        var y   = parseInt(yearEl.value, 10);
        var now = new Date().getFullYear();
        if (y < 1970 || y > now + 1) {
          var wrap  = yearEl.closest('.sc-booking-form__field');
          var errEl = wrap ? wrap.querySelector('.sc-booking-form__field-error') : null;
          if (wrap)  wrap.classList.add('sc-booking-form__field--error');
          if (errEl) errEl.textContent = 'Enter a valid year (1970–' + now + ')';
        }
      });
    }

    /* ── Validate a single field ── */
    function validateField(field) {
      var wrap = field.closest('.sc-booking-form__field');
      if (!wrap) return true;
      var val = field.value.trim();

      if (field.hasAttribute('required') && val === '') {
        wrap.classList.add('sc-booking-form__field--error');
        return false;
      }
      if (field.type === 'email' && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        wrap.classList.add('sc-booking-form__field--error');
        return false;
      }
      wrap.classList.remove('sc-booking-form__field--error');
      return true;
    }

    /* ── Validate all required fields within a step container ── */
    function validateStep(stepEl) {
      var valid = true;
      stepEl.querySelectorAll('input[required], select[required], textarea[required]').forEach(function (f) {
        if (!validateField(f)) valid = false;
      });
      return valid;
    }

    /* ── Clear form-level error ── */
    function clearFormError() {
      if (formErr) formErr.classList.remove('sc-booking-form__form-error--visible');
    }

    function showFormError(msg) {
      if (formErr) {
        formErr.textContent = msg;
        formErr.classList.add('sc-booking-form__form-error--visible');
      }
    }

    /* Clear field errors on any input */
    formEl.querySelectorAll('input, select, textarea').forEach(function (f) {
      f.addEventListener('input', function () {
        var wrap = f.closest('.sc-booking-form__field');
        if (wrap) wrap.classList.remove('sc-booking-form__field--error');
        clearFormError();
      });
    });

    /* ── Next button: validate step 1 then advance ── */
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!step1El) return;
        if (!validateStep(step1El)) {
          showFormError('>>> PLEASE FILL IN ALL REQUIRED FIELDS.');
          return;
        }
        showStep(2);
      });
    }

    /* ── Back button: return to step 1 ── */
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        showStep(1);
      });
    }

    /* ── Form submit: validate step 2 then POST payload ── */
    formEl.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!step2El) return;

      if (!validateStep(step2El)) {
        showFormError('>>> PLEASE FILL IN ALL REQUIRED FIELDS.');
        return;
      }

      if (!webhookUrl) {
        showFormError('>>> WEBHOOK URL NOT CONFIGURED — CONTACT SITE ADMIN.');
        return;
      }

      /* Loading state */
      if (submitBtn) submitBtn.disabled = true;
      if (submitText) submitText.textContent = 'SUBMITTING...';

      /* Build payload — collect from both steps */
      var year  = ((formEl.querySelector('[name="year"]')  || {}).value || '').trim();
      var make  = ((formEl.querySelector('[name="make"]')  || {}).value || '').trim();
      var model = ((formEl.querySelector('[name="model"]') || {}).value || '').trim();

      var payload = {
        name:      ((formEl.querySelector('[name="name"]')      || {}).value || '').trim(),
        email:     ((formEl.querySelector('[name="email"]')     || {}).value || '').trim(),
        phone:     ((formEl.querySelector('[name="phone"]')     || {}).value || '').trim(),
        instagram: ((formEl.querySelector('[name="instagram"]') || {}).value || '').trim(),
        year:      year,
        make:      make,
        model:     model,
        vehicle:   [year, make, model].filter(Boolean).join(' '),
        service:   ((formEl.querySelector('[name="service"]')   || {}).value || '').trim(),
        stage:     ((formEl.querySelector('[name="stage"]')     || {}).value || '').trim(),
        date:      ((formEl.querySelector('[name="date"]')      || {}).value || '').trim(),
        notes:     ((formEl.querySelector('[name="notes"]')     || {}).value || '').trim()
      };

      fetch(webhookUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Server returned ' + res.status);
          /* Success — hide form, show confirmation */
          formEl.style.display = 'none';
          if (successEl) successEl.style.display = 'block';
        })
        .catch(function () {
          if (submitBtn) submitBtn.disabled = false;
          if (submitText) submitText.textContent = 'LOCK MY BUILD SLOT';
          showFormError('>>> SOMETHING WENT WRONG. DM US ON INSTAGRAM TO BOOK.');
        });
    });
  }

  /* ── Boot ── */
  function boot() {
    document.querySelectorAll('.sc-booking-form__form').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-init */
  document.addEventListener('shopify:section:load', function (e) {
    var f = e.target.querySelector('.sc-booking-form__form');
    if (f) initForm(f);
  });

})();
